/**
 * React Query hooks for the Workflows API (SPEC PX-4 — workflow-builder).
 *
 * Mirrors `hooks/useSequences.ts`: a `workflowKeys` factory + list / get /
 * metadata queries and create / update / delete mutations (mutations invalidate
 * the list on success). @tanstack/react-query v5.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  WorkflowCreate,
  WorkflowDefinition,
  WorkflowListItem,
  WorkflowMetadata,
  WorkflowUpdate,
} from '@/types/workflow';
import {
  createWorkflow,
  deleteWorkflow,
  getWorkflow,
  getWorkflowMetadata,
  listWorkflows,
  updateWorkflow,
} from '../workflows';

export const workflowKeys = {
  all: ['workflows'] as const,
  list: () => [...workflowKeys.all, 'list'] as const,
  detail: (id: string) => [...workflowKeys.all, 'detail', id] as const,
  metadata: () => [...workflowKeys.all, 'metadata'] as const,
};

/** List the tenant's workflow definitions. */
export function useWorkflows(): UseQueryResult<WorkflowListItem[], Error> {
  return useQuery({
    queryKey: workflowKeys.list(),
    queryFn: listWorkflows,
  });
}

/** Get a single workflow definition by id. */
export function useWorkflow(id: string): UseQueryResult<WorkflowDefinition, Error> {
  return useQuery({
    queryKey: workflowKeys.detail(id),
    queryFn: () => getWorkflow(id),
    enabled: !!id,
  });
}

/** The registry-driven palette descriptor (rarely changes — cached). */
export function useWorkflowMetadata(): UseQueryResult<WorkflowMetadata, Error> {
  return useQuery({
    queryKey: workflowKeys.metadata(),
    queryFn: getWorkflowMetadata,
    staleTime: 1000 * 60 * 10,
  });
}

/** Create a workflow (always active:false on first create). */
export function useCreateWorkflow(): UseMutationResult<
  WorkflowDefinition,
  Error,
  WorkflowCreate,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createWorkflow,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.list() });
    },
  });
}

/** Update a workflow (also the activate path via {active}). */
export function useUpdateWorkflow(): UseMutationResult<
  WorkflowDefinition,
  Error,
  { id: string; data: WorkflowUpdate },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => updateWorkflow(id, data),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(workflowKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: workflowKeys.list() });
    },
  });
}

/** Delete a workflow definition. */
export function useDeleteWorkflow(): UseMutationResult<void, Error, string, unknown> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteWorkflow,
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: workflowKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.list() });
    },
  });
}
